export interface PascalsTriangleSelection {
  readonly n: number;
  readonly r: number;
}

export interface PascalsTriangleRowHighlight {
  readonly row: number;
  readonly color: string;
  readonly label?: string;
}

export interface PascalsTriangleParams {
  readonly rows: number;
}

export const DEFAULT_PASCALS_TRIANGLE_PARAMS: PascalsTriangleParams = {
  rows: 11,
};

/** A highlight function that returns a color group key or null for each cell. */
export type PascalsTriangleHighlightFn = (n: number, r: number) => string | null;

/** Definition of a useful property displayed in the properties table. */
export interface PascalsTrianglePropertyDef {
  readonly id: string;
  /** LaTeX string for the property formula. */
  readonly propertyLatex: string;
  /** Plain text explanation. */
  readonly meaning: string;
  /** LaTeX string for a concrete example. */
  readonly exampleLatex: string;
  /** Returns a color group key ('primary', 'secondary', etc.) or null per cell. */
  readonly highlight: PascalsTriangleHighlightFn;
}

export const DEFAULT_PROPERTIES: readonly PascalsTrianglePropertyDef[] = [
  {
    id: 'first-last',
    propertyLatex: String.raw`\binom{n}{0} = \binom{n}{n} = 1`,
    meaning: 'The first and last entry of every row is 1.',
    exampleLatex: String.raw`\binom{3}{0} = \binom{3}{3} = 1`,
    highlight: (n, r) => {
      return r === 0 || r === n ? 'primary' : null;
    },
  },
  {
    id: 'symmetry',
    propertyLatex: String.raw`\binom{n}{r} = \binom{n}{n-r}`,
    meaning: 'Each row is symmetric about its centre.',
    exampleLatex: String.raw`\binom{4}{1} = \binom{4}{3} = 4`,
    highlight: (n, r) => {
      if (n < 2) {
        return null;
      }
      const mid = n / 2;
      if (r === mid) {
        return null;
      }
      return r < mid ? 'primary' : 'secondary';
    },
  },
  {
    id: 'second-entry',
    propertyLatex: String.raw`\binom{n}{1} = n`,
    meaning: 'The second entry of row n equals n.',
    exampleLatex: String.raw`\binom{5}{1} = 5`,
    highlight: (n, r) => {
      return r === 1 && n >= 1 ? 'primary' : null;
    },
  },
];
